import request from 'api/requestUtils'
import type { MultiCartPayloadItem } from 'api/cart/multiCartPayloadMappers'
import { mapCheckoutItemToMultiCartPayloadItem } from 'api/cart/multiCartPayloadMappers'
import type { paths } from '@luxuryescapes/contract-trip'
import { baseUri } from './utils'

type UpsertFlightCartOperation =
  paths['/api/trips/v3/chats/{chatId}/items/{itemId}/flight-cart']['post']
type ContractFlightCartRequest =
  UpsertFlightCartOperation['requestBody']['content']['application/json']
type ContractFlightCartErrorResponse =
  | UpsertFlightCartOperation['responses']['409']['content']['application/json']
  | UpsertFlightCartOperation['responses']['422']['content']['application/json']
  | UpsertFlightCartOperation['responses']['502']['content']['application/json']
  | UpsertFlightCartOperation['responses']['503']['content']['application/json']

export type TripChatFlightCartPayloadItem =
  Extract<MultiCartPayloadItem, { itemType: 'flight' }>
  & ContractFlightCartRequest['flightPayload']

export type FlightCartLegMetadata =
  NonNullable<NonNullable<ContractFlightCartRequest['legMetadata']>['outbound']>

export type FlightCartRequest = Omit<ContractFlightCartRequest, 'flightPayload' | 'legMetadata'> & {
  flightPayload: TripChatFlightCartPayloadItem
  legMetadata?: {
    outbound: FlightCartLegMetadata
    return?: FlightCartLegMetadata
  }
}

export type FlightCartIntent = FlightCartRequest['intent']

export type FlightCartReactivationMode = NonNullable<FlightCartRequest['reactivationMode']>

export type FlightCartResult =
  UpsertFlightCartOperation['responses']['200']['content']['application/json']['result']

export type FlightCartErrorCode = ContractFlightCartErrorResponse['code']

export type FlightCartErrorResponse = ContractFlightCartErrorResponse

export function isTripChatFlightCartPayloadItem(
  payload: MultiCartPayloadItem,
): payload is TripChatFlightCartPayloadItem {
  return payload.itemType === 'flight'
}

export function mapCheckoutFlightItemToTripChatFlightCartPayload(
  checkoutItem: App.Checkout.FlightItem,
): TripChatFlightCartPayloadItem {
  const mappedItem = mapCheckoutItemToMultiCartPayloadItem(checkoutItem)

  if (!isTripChatFlightCartPayloadItem(mappedItem)) {
    throw new Error('Expected mapped checkout item to be a flight cart payload item')
  }

  return mappedItem
}

const FLIGHT_CART_ERROR_CODES: ReadonlySet<string> = new Set<FlightCartErrorCode>([
  'STALE_ITEM_STATE',
  'PAIRING_CHANGED',
  'CART_ITEM_ALREADY_LINKED',
  'SPLIT_RETURN_CART_IDENTITY',
  'ASSISTANT_TURN_ACTIVE',
  'CART_REPLACE_ROLLBACK_FAILED',
  'MULTICART_CLAIM_LOST',
  'INVALID_INTENT_STATE',
  'INVALID_FLIGHT_SOURCE',
  'INVALID_RETURN_PAIRING',
  'ROUTE_MISMATCH',
  'DATE_MISMATCH',
  'UNSUPPORTED_REACTIVATION_MODE',
  'INVALID_LEG_METADATA',
  'INVALID_FLIGHT_PAYLOAD',
  'BOOKED_FLIGHT_NOT_CARTABLE',
  'CUSTOM_FLIGHT_NOT_CARTABLE',
  'CART_DEPENDENCY_FAILED',
  'FLIGHT_DEPENDENCY_FAILED',
])

function hasFlightCartErrorStatus(status: unknown): status is FlightCartErrorResponse['status'] {
  return status === 409 || status === 422 || status === 502 || status === 503
}

function hasFlightCartErrorCode(code: unknown): code is FlightCartErrorCode {
  return typeof code === 'string' && FLIGHT_CART_ERROR_CODES.has(code)
}

export function isFlightCartErrorResponse(error: unknown): error is FlightCartErrorResponse {
  if (!error || typeof error !== 'object') return false
  if (!('status' in error) || !hasFlightCartErrorStatus(error.status)) return false
  if (!('code' in error) || !hasFlightCartErrorCode(error.code)) return false
  if (!('message' in error) || typeof error.message !== 'string') return false
  return 'requiresRefetch' in error && error.requiresRefetch === true
}

export async function upsertTripChatFlightCartItem(
  chatId: number,
  itemId: number,
  requestBody: FlightCartRequest,
): Promise<FlightCartResult> {
  if (!isTripChatFlightCartPayloadItem(requestBody.flightPayload)) {
    throw new Error('Flight cart payload must be a checkout flight item')
  }

  const response = await request.post<App.ApiResponse<FlightCartResult>, FlightCartRequest>(
    `${baseUri}/v3/chats/${chatId}/items/${itemId}/flight-cart`,
    requestBody,
    { credentials: 'include' },
  )

  return response.result
}
